import { useMemo } from 'react';
import type { FeedMixItem, PromoCampaignItem } from '../../types/feedMix';
import type { PromoCampaignsResponse } from '../../types/feed';
import { RestaurantFeedCard } from './RestaurantFeedCard';
import { TopDishCard } from './TopDishCard';
import { PromoCollabCard } from './PromoCollabCard';
import { FeaturedRestaurantCard } from './FeaturedRestaurantCard';
import { VenueFeedCard } from './VenueFeedCard';
import './MixFeedRenderer.css';

interface Props {
  items: FeedMixItem[];
}

type Row =
  | { kind: 'full'; item: FeedMixItem; key: string }
  | { kind: 'pair'; items: FeedMixItem[]; key: string };

function isFullWidth(type: FeedMixItem['type']): boolean {
  return type === 'promo' || type === 'featured-restaurant';
}

function itemKey(item: FeedMixItem, idx: number): string {
  const id = (item.data as { id?: string }).id ?? `idx-${idx}`;
  return `${item.type}-${id}`;
}

type PromoCardItem = PromoCampaignsResponse['items'][number];

function adaptPromo(p: PromoCampaignItem): PromoCardItem {
  const productA = p.productA ?? { id: '', name: '', imageUrl: null, price: 0 };
  const productB = p.productB ?? { id: '', name: '', imageUrl: null, price: 0 };
  const priceTotal = productA.price + productB.price;
  return {
    id: p.id,
    title: p.promoText ?? `${productA.name} + ${productB.name}`,
    subtitle: null,
    imageUrl: productA.imageUrl ?? productB.imageUrl,
    priceTotal,
    priceOriginal: priceTotal,
    discountPct: null,
    venueId: null,
    productA,
    productB,
  };
}

export function MixFeedRenderer({ items }: Props) {
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    let buf: FeedMixItem[] = [];
    let bufStart = 0;
    const flush = () => {
      if (buf.length === 0) return;
      const key = buf.map((it, i) => itemKey(it, bufStart + i)).join('|');
      out.push({ kind: 'pair', items: buf, key });
      buf = [];
    };
    items.forEach((item, i) => {
      if (isFullWidth(item.type)) {
        flush();
        out.push({ kind: 'full', item, key: itemKey(item, i) });
      } else {
        if (buf.length === 0) bufStart = i;
        buf.push(item);
        if (buf.length === 2) flush();
      }
    });
    flush();
    return out;
  }, [items]);

  return (
    <div className="mfr-list" role="list">
      {rows.map((row) =>
        row.kind === 'full' ? (
          <div key={row.key} className="mfr-row-full" role="listitem">
            {renderItem(row.item)}
          </div>
        ) : (
          <div key={row.key} className="mfr-row-pair" role="listitem">
            {row.items.map((it, i) => (
              <div key={itemKey(it, i)}>{renderItem(it)}</div>
            ))}
          </div>
        ),
      )}
    </div>
  );
}

function renderItem(item: FeedMixItem): JSX.Element | null {
  switch (item.type) {
    case 'promo':
      return <PromoCollabCard item={adaptPromo(item.data)} />;
    case 'featured-restaurant':
      return <FeaturedRestaurantCard r={item.data} />;
    case 'restaurant':
      return <RestaurantFeedCard r={item.data} />;
    case 'top-dish':
      return <TopDishCard item={item.data} />;
    case 'venue':
      return <VenueFeedCard v={item.data} />;
    default:
      return null;
  }
}
